import { CloseButton, Loader } from 'components';
import PopupWrapper from 'components/Popups/Wrapper';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComposerSendIcon } from 'static';

import { formatBytes } from '../Attachments';
import { bodyToPreviewHtml } from '../RichTextEditor/serialize';
import {
  StyledAttachmentItem,
  StyledAttachmentList,
  StyledAttachmentName,
  StyledAttachmentSize,
  StyledBackButton,
  StyledBody,
  StyledConfirmButton,
  StyledFromEmail,
  StyledFromName,
  StyledMetaLabel,
  StyledMetaRow,
  StyledMetaValue,
  StyledMoreButton,
  StyledPreview,
  StyledPreviewCount,
  StyledPreviewFooter,
  StyledPreviewFrame,
  StyledPreviewHeader,
  StyledPreviewHeading,
  StyledPreviewSheet,
  StyledPreviewTitle,
  StyledRecipientChip,
  StyledRecipientChipLabel,
  StyledRecipientDirectory,
  StyledRecipientEmail,
  StyledRecipientList,
  StyledRecipientName,
  StyledRecipientRow,
  StyledRecipientTip,
  StyledSubject,
} from './styled';

const COLLAPSED_RECIPIENT_LIMIT = 3;

export interface PreviewRecipient {
  name: string;
  detail?: string;
}

export interface PreviewAttachment {
  name: string;
  size: number;
}

interface EmailPreviewProps {
  isOpen: boolean;
  sending: boolean;
  confirmDisabled: boolean;
  fromName: string;
  fromEmail?: string;
  recipients: PreviewRecipient[];
  subject: string;
  body: string;
  attachments: PreviewAttachment[];
  onClose: () => void;
  onConfirm: () => void;
  deferConfirmShortcut?: boolean;
}

const EmailPreview: FC<EmailPreviewProps> = ({
  isOpen,
  sending,
  confirmDisabled,
  fromName,
  fromEmail,
  recipients,
  subject,
  body,
  attachments,
  onClose,
  onConfirm,
  deferConfirmShortcut = false,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const onConfirmRef = useRef(onConfirm);
  const [expanded, setExpanded] = useState(false);

  onCloseRef.current = onClose;
  onConfirmRef.current = onConfirm;

  useEffect(() => {
    if (!isOpen) {
      setExpanded(false);
      return undefined;
    }

    dialogRef.current?.focus();

    // The keypress that opened the preview must not also confirm the send.
    let confirmArmed = !deferConfirmShortcut;
    let shortcutRepeated = false;
    const armIfReleased = () => {
      if (!shortcutRepeated) confirmArmed = true;
    };
    const releaseTimer = window.setTimeout(armIfReleased, 0);
    const handleKeyUp = (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' ||
        event.key === 'Meta' ||
        event.key === 'Control'
      ) {
        confirmArmed = true;
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !sending) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        if (event.repeat) {
          shortcutRepeated = true;
          return;
        }
        if (!confirmArmed || sending || confirmDisabled) return;
        onConfirmRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      window.clearTimeout(releaseTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [isOpen, sending, confirmDisabled, deferConfirmShortcut]);

  const visibleRecipients =
    expanded || recipients.length <= COLLAPSED_RECIPIENT_LIMIT
      ? recipients
      : recipients.slice(0, COLLAPSED_RECIPIENT_LIMIT);
  const hiddenCount = recipients.length - visibleRecipients.length;

  return (
    <PopupWrapper isOpen={isOpen}>
      <StyledPreviewFrame
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !sending) onClose();
        }}
      >
        <StyledPreview
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-preview-title"
          tabIndex={-1}
        >
          <StyledPreviewHeader>
            <StyledPreviewHeading>
              <StyledPreviewTitle id="email-preview-title">
                {t('email.previewTitle')}
              </StyledPreviewTitle>
              <StyledPreviewCount>
                {t(
                  recipients.length === 1
                    ? 'email.previewRecipients_one'
                    : 'email.previewRecipients_other',
                  { count: recipients.length },
                )}
              </StyledPreviewCount>
            </StyledPreviewHeading>
            <CloseButton onClick={onClose} hidden={sending} />
          </StyledPreviewHeader>

          <StyledPreviewSheet>
            <StyledMetaRow>
              <StyledMetaLabel>{t('common.from')}</StyledMetaLabel>
              <StyledMetaValue>
                <StyledFromName>{fromName}</StyledFromName>
                {fromEmail && <StyledFromEmail>{fromEmail}</StyledFromEmail>}
              </StyledMetaValue>
            </StyledMetaRow>

            <StyledMetaRow>
              <StyledMetaLabel>{t('email.to')}</StyledMetaLabel>
              <StyledMetaValue>
                {expanded ? (
                  <StyledRecipientDirectory>
                    {recipients.map((recipient, index) => {
                      const email =
                        recipient.detail && recipient.detail !== recipient.name
                          ? recipient.detail
                          : '';
                      return (
                        <StyledRecipientRow
                          key={`${recipient.name}-${recipient.detail || index}`}
                        >
                          <StyledRecipientName>
                            {recipient.name}
                          </StyledRecipientName>
                          {email && (
                            <StyledRecipientEmail>{email}</StyledRecipientEmail>
                          )}
                        </StyledRecipientRow>
                      );
                    })}
                  </StyledRecipientDirectory>
                ) : (
                  <StyledRecipientList>
                    {visibleRecipients.map((recipient, index) => {
                      const email =
                        recipient.detail && recipient.detail !== recipient.name
                          ? recipient.detail
                          : '';
                      return (
                        <StyledRecipientChip
                          key={`${recipient.name}-${recipient.detail || index}`}
                          tabIndex={email ? 0 : undefined}
                        >
                          <StyledRecipientChipLabel>
                            {recipient.name}
                          </StyledRecipientChipLabel>
                          {email && (
                            <StyledRecipientTip>{email}</StyledRecipientTip>
                          )}
                        </StyledRecipientChip>
                      );
                    })}
                  </StyledRecipientList>
                )}
                {(hiddenCount > 0 || expanded) &&
                  recipients.length > COLLAPSED_RECIPIENT_LIMIT && (
                    <StyledMoreButton
                      type="button"
                      onClick={() => setExpanded((open) => !open)}
                    >
                      {expanded
                        ? t('email.showLess')
                        : t('email.moreRecipients', { count: hiddenCount })}
                    </StyledMoreButton>
                  )}
              </StyledMetaValue>
            </StyledMetaRow>

            <StyledSubject>{subject}</StyledSubject>
            <StyledBody
              dangerouslySetInnerHTML={{ __html: bodyToPreviewHtml(body) }}
            />

            {attachments.length > 0 && (
              <StyledAttachmentList>
                {attachments.map((file) => (
                  <StyledAttachmentItem key={file.name}>
                    <StyledAttachmentName>{file.name}</StyledAttachmentName>
                    <StyledAttachmentSize>
                      {formatBytes(file.size)}
                    </StyledAttachmentSize>
                  </StyledAttachmentItem>
                ))}
              </StyledAttachmentList>
            )}
          </StyledPreviewSheet>

          <StyledPreviewFooter>
            <StyledBackButton
              type="button"
              onClick={onClose}
              disabled={sending}
            >
              {t('communitySettings.notificationPopupBack')}
            </StyledBackButton>
            <StyledConfirmButton
              type="button"
              onClick={onConfirm}
              disabled={sending || confirmDisabled}
              aria-busy={sending}
            >
              {sending ? (
                <Loader type="button" size="22px" thickness={2} />
              ) : (
                <>
                  {t('auth.send')}
                  <ComposerSendIcon />
                </>
              )}
            </StyledConfirmButton>
          </StyledPreviewFooter>
        </StyledPreview>
      </StyledPreviewFrame>
    </PopupWrapper>
  );
};

export default EmailPreview;
